import Select from "react-select";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

export function Selector({
  color,
  options = [],
  className,
  field, ////////key
  value = null,
  onChange: handleChange,
  placeholder,
  autoFillNum = null,
  translation,
  disabled = false,
  width,
}) {
  const { t } = useTranslation(translation);

  const isCustomEFDropdown = useMemo(
    () => (options ?? []).some((group) => group?.isCustomEFGroup),
    [options],
  );
  ////////翻譯過後的options

  const translatedOptions = useMemo(() => {
    if ((options ?? []).some((opt) => Array.isArray(opt?.options))) {
      return (options ?? []).map((group) => ({
        ...group,
        label: t(group.label),
        options: group.options.map((opt) => ({
          ...opt,
          label:
            (opt.labelKey && t(opt.labelKey)) ||
            (typeof opt.label === "string" ? t(opt.label) : opt.label) ||
            t(String(opt.value)),
        })),
      }));
    }
    return (options ?? []).map((opt) => {
      if (typeof opt === "string") {
        return { label: t(opt), value: opt }; // 字串當作 i18n key
      }
      // 物件：優先吃 labelKey，其次把 label 視為 i18n key，最後維持原 label
      const label =
        (opt.labelKey && t(opt.labelKey)) ||
        (typeof opt.label === "string" ? t(opt.label) : opt.label) ||
        t(String(opt.value));
      return { ...opt, label };
    });
    // 目的：語系切換時能重新翻譯
  }, [options, t]);

  ////////選中的option
  const selectedOption = useMemo(
    () =>
      translatedOptions?.flatMap((opt) => opt.options ?? [opt]).find((opt) => {
        const ov = opt.value;
        const vv = value;
        // 1. 如果都是数组，就用 toString() 或者逐项比较
        if (Array.isArray(ov) && Array.isArray(vv)) {
          return ov.toString() === vv.toString();
        }
        // 2. 否则直接做严格比较（支持 string、number、boolean）
        return ov.toString() === vv.toString();
      }) || null,
    [translatedOptions, value]
  );

  ////////自動帶入指定的資料
  useEffect(() => {
    if (autoFillNum === null) return;
    if (value !== null && value !== undefined && value !== "") return;
    if (!options || options.length === 0) return;

    const selectableOptions = options
      .flatMap((option) => option?.options ?? [option])
      .filter((option) => !option?.isDisabled);
    const target = selectableOptions[autoFillNum];

    if (target) {
      handleChange(field, target.value, target);
    }
  }, [autoFillNum, options, value, field]);

  ////////react-select styles
  const selectProps = useMemo(() => {
    const styles = {
      control: (base, { isFocused }) => ({
        ...base,
        cursor: "pointer",
        width: width || "350px",
        height: "43px",
        border: color || "1px solid #ccc",
        borderRadius: "4px",
        padding: 0,
        display: "flex",
        alignItems: "center",
        "&:hover": {
          borderColor: color || "#ccc", // 保持原色
          boxShadow: "none",
          outline: "none",
        },
        ...(isFocused
          ? {
              borderColor: color || "#ccc",
              boxShadow: "none",
            }
          : {}),
      }),
      input: (base) => ({
        ...base,
        padding: 0,
        margin: 0,
        lineHeight: "43px",
        fontSize: "14px",
      }),
      menu: (base) => ({
        ...base,
        padding: 0,
        width: width || "350px",
        marginTop: "4px",
        borderRadius: isCustomEFDropdown ? "8px" : "4px",
        border: isCustomEFDropdown
          ? "1px solid #d8deea"
          : "1px solid #ccc",
        boxShadow: isCustomEFDropdown
          ? "0 8px 24px rgba(36, 50, 71, 0.12)"
          : "0 2px 5px rgba(0, 0, 0, 0.1)",
        ...(isCustomEFDropdown ? { overflow: "hidden" } : {}),
        zIndex: 600,
      }),
      menuPortal: (base) => ({ ...base, zIndex: 600 }),
      valueContainer: (base) => ({
        ...base,
        flex: 1,
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        padding: "0 10px",
        display: "flex",
        alignItems: "center",
      }),
      menuList: (base) => ({
        ...base,
        maxHeight: "200px",
        overflowY: "auto",
        ...(isCustomEFDropdown ? { padding: "6px" } : {}),
      }),
      group: (base) =>
        isCustomEFDropdown
          ? {
              ...base,
              paddingTop: "4px",
              paddingBottom: "2px",
            }
          : base,
      groupHeading: (base) =>
        isCustomEFDropdown
          ? {
              ...base,
              margin: "0",
              padding: "6px 10px 4px",
              color: "#8a93a3",
              fontSize: "10px",
              fontWeight: 700,
              lineHeight: 1.3,
              letterSpacing: 0,
              textTransform: "uppercase",
            }
          : base,
      option: (base, state) =>
        isCustomEFDropdown
          ? {
              ...base,
              backgroundColor: state.isSelected
                ? "#edf3ff"
                : state.isFocused
                  ? "#f4f7fb"
                  : "#fff",
              color: "#243247",
              minHeight: "34px",
              padding: "6px 10px",
              borderRadius: "6px",
              cursor: state.isDisabled ? "not-allowed" : "pointer",
              "&:hover": {
                backgroundColor: state.isDisabled ? "#f4f7fb" : "#edf3ff",
              },
            }
          : {
              ...base,
              backgroundColor: state.isFocused ? "#e0e0e0" : "#fff",
              color: state.isSelected ? "rgb(20, 100, 200)" : "#333",
              padding: "10px",
              cursor: "pointer",
              "&:hover": {
                backgroundColor: "#d5d5d5",
              },
            },
      singleValue: (base) => ({
        ...base,
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        lineHeight: "43px",
        ...(isCustomEFDropdown
          ? {
              display: "flex",
              alignItems: "center",
              width: "calc(100% - 4px)",
              maxWidth: "none",
            }
          : { maxWidth: "270px" }),
      }),
      placeholder: (base) => ({
        ...base,
        margin: 0, // 去掉默认 margin
        display: "flex",
        alignItems: "center", // 再确保垂直居中
        height: "43px", // 和 control 同高
        lineHeight: "43px",
        overflow: "hidden", // 超出截断
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        color: "#999",
      }),
      indicatorsContainer: (base) => ({
        ...base,
        height: "43px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        paddingRight: "8px",
      }),
    };

    return {
      styles,
      noOptionsMessage: () => t("No related options"),
      components: { IndicatorSeparator: () => null },
      isOptionDisabled: (option) => Boolean(option.isDisabled),
      formatOptionLabel: (option, { context }) => {
        if (!isCustomEFDropdown) {
          return (
            <div
              style={{
                display: "flex",
                minWidth: 0,
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                color: option.isDeleted ? "#8b93a0" : undefined,
                textDecoration: option.isDeleted ? "line-through" : "none",
              }}
            >
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {option.label}
              </span>
              {context === "menu" && option.hasNewUpdate && (
                <span
                  style={{
                    flex: "0 0 auto",
                    padding: "2px 6px",
                    color: "#a33a00",
                    background: "#fff0df",
                    border: "1px solid #f4bd82",
                    borderRadius: 10,
                    fontSize: 10,
                    fontWeight: 700,
                  }}
                >
                  {t("New Update")}
                </span>
              )}
            </div>
          );
        }

        return (
        <div
          style={{
            display: "flex",
            width: "100%",
            minWidth: 0,
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            color: option.isDeleted ? "#8b93a0" : undefined,
          }}
        >
          <span
            style={{
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              textDecoration: option.isDeleted ? "line-through" : "none",
            }}
          >
            {option.label}
          </span>
          <span
            style={{ display: "flex", minWidth: 0, flex: "0 1 auto", gap: 8 }}
          >
            {option.isDeleted ? (
              <span
                style={{
                  padding: "2px 8px",
                  color: "#b42318",
                  background: "#fff1f0",
                  border: "1px solid #ffc9c4",
                  borderRadius: 10,
                  fontSize: 10,
                  fontWeight: 700,
                  lineHeight: 1.3,
                }}
              >
                {t("Deleted")}
              </span>
            ) : (
              <>
                {option.isShared && (
                  <span
                    title={
                      option.creatorName
                        ? `${t("Shared")} (${option.creatorName})`
                        : t("Shared")
                    }
                    style={{
                      minWidth: 0,
                      maxWidth: 150,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      padding: "2px 8px",
                      color: "#38753c",
                      background: "#eef7ec",
                      border: "1px solid #c9dfc5",
                      borderRadius: 10,
                      fontSize: 10,
                      fontWeight: 700,
                      lineHeight: 1.3,
                    }}
                  >
                    {option.creatorName
                      ? `${t("Shared")} (${option.creatorName})`
                      : t("Shared")}
                  </span>
                )}
                {option.hasNewUpdate && (
                  <span
                    style={{
                      padding: "2px 8px",
                      color: "#3030a3",
                      background: "#f0f1ff",
                      border: "1px solid #c9ccf8",
                      borderRadius: 10,
                      fontSize: 10,
                      fontWeight: 700,
                      lineHeight: 1.3,
                    }}
                  >
                    {t("New Update")}
                  </span>
                )}
              </>
            )}
          </span>
        </div>
        );
      },
    };
  }, [width, color, t, isCustomEFDropdown]);

  return (
    <div className={className}>
      <Select
        menuPortalTarget={document.body}
        menuPlacement="auto" //自動判斷空間往上或往下開
        {...selectProps}
        value={selectedOption}
        options={translatedOptions}
        onChange={(opt) => handleChange(field, opt.value, opt)}
        placeholder={placeholder}
        isDisabled={disabled}
      />
    </div>
  );
}
